/**
 * Escalado de recetas (Req. 9.4 a 9.8).
 *
 * CODIGO PURO. El escalado opera sobre las cantidades en unidad base y NO
 * aplica redondeos intermedios: redondear a medio camino desvia el costo, como
 * quedo demostrado en las pruebas de money.
 */

import { ErrorValidacion } from './errors';
import { normalizarCantidad } from './units';

export interface ItemEscalable {
  ingredientId: string;
  cantidadBase: number;
  /**
   * false = cantidad fija que no cambia con las porciones.
   *
   * Pensado para la sal y las especias, que no se duplican al duplicar el
   * guiso. Hoy la interfaz siempre lo deja en true; el campo existe para no
   * tener que cambiar el modelo cuando se ofrezca.
   */
  escala: boolean;
}

export interface ItemEscalado<T> {
  item: T;
  /** Cantidad ya escalada a las porciones solicitadas. */
  cantidadBase: number;
}

/**
 * Factor por el que se multiplican las cantidades.
 *
 * Rendimiento 10, se piden 5  -> 0.5   (la mitad)
 * Rendimiento 10, se piden 8  -> 0.8   (el 80 %)
 */
export function factorEscala(porcionesSolicitadas: number, rendimientoPorciones: number): number {
  if (!Number.isFinite(porcionesSolicitadas) || porcionesSolicitadas < 0) {
    throw new ErrorValidacion('Las porciones solicitadas deben ser un numero no negativo.');
  }
  if (!Number.isFinite(rendimientoPorciones) || rendimientoPorciones <= 0) {
    throw new ErrorValidacion('El rendimiento de la receta debe ser mayor que cero.');
  }
  return porcionesSolicitadas / rendimientoPorciones;
}

/** Escala las cantidades de una receta a las porciones que se van a producir. */
export function escalarItems<T extends ItemEscalable>(
  items: readonly T[],
  porcionesSolicitadas: number,
  rendimientoPorciones: number,
): Array<ItemEscalado<T>> {
  const factor = factorEscala(porcionesSolicitadas, rendimientoPorciones);

  return items.map((item) => ({
    item,
    cantidadBase: item.escala
      ? normalizarCantidad(item.cantidadBase * factor)
      : normalizarCantidad(item.cantidadBase),
  }));
}
